import { useEffect, useRef } from 'react';

export const useScrollAnimation = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const animatedElementsRef = useRef<Set<Element>>(new Set());

  useEffect(() => {
    const setupObserver = () => {
      const fadeUpElements = document.querySelectorAll('.fadeUp');
      
      if (fadeUpElements.length === 0) {
        return;
      }

      // Disconnect existing observer if any
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const element = entry.target as HTMLElement;
            
            // Only animate if element hasn't been animated yet
            if (entry.isIntersecting && !animatedElementsRef.current.has(element)) {
              // Element is entering viewport for the first time - show it
              element.classList.add('fadeUp-visible');
              element.classList.remove('fadeUp-hidden');
              
              // Mark as animated so it won't re-animate
              animatedElementsRef.current.add(element);
              
              // Stop observing this element since it's already been animated
              observerRef.current?.unobserve(element);
            }
            // If element leaves viewport, we don't do anything - it stays visible
          });
        },
        {
          root: null,
          rootMargin: '-2% 0px -2% 0px', // Trigger when element is 10% into viewport
          threshold: 0.1 // Trigger at 10% visibility
        }
      );

      // Observe all fadeUp elements that haven't been animated yet
      fadeUpElements.forEach((element) => {
        // Only observe if not already animated
        if (!animatedElementsRef.current.has(element)) {
          observerRef.current?.observe(element);
        }
      });
    };

    // Initial setup
    setupObserver();

    // Re-run when DOM changes (for dynamically added elements and route changes)
    const checkForNewElements = () => {
      const fadeUpElements = document.querySelectorAll('.fadeUp');
      fadeUpElements.forEach((element) => {
        // Only observe if not already animated and not already being observed
        if (!animatedElementsRef.current.has(element) && !element.hasAttribute('data-observed')) {
          element.setAttribute('data-observed', 'true');
          observerRef.current?.observe(element);
        }
      });
    };

    // Use MutationObserver to watch for new elements
    const mutationObserver = new MutationObserver(() => {
      checkForNewElements();
      // Also re-setup observer when route changes (elements might be removed/re-added)
      setupObserver();
    });
    
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check periodically for route changes
    const intervalId = setInterval(checkForNewElements, 500);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      mutationObserver.disconnect();
      clearInterval(intervalId);
      // Note: We don't clear animatedElementsRef on cleanup because we want animations
      // to persist until page refresh (which will reset everything)
    };
  }, []); // Run once on mount
};

